import { memo } from "react";
import { type GoogleMarker } from "~/domains/salesPartner/types/SalesPartnerTypes";
import { containsFeature, SalesPartnerFeature } from "~/domains/salesPartner/utils/transformers";
import classNames from "classnames";
import { Logos } from "~/common/components/Logos";
import { Card } from "~/features/configurator/Linear/components/Card";

import { useTranslation } from "react-i18next";
import { Radio } from "~/common/mui";
import Tag from "~/common/components/Tag/Tag";
import { Switch } from "~/common/components/Switch/Switch";

interface Props {
  className?: string;
  marker: GoogleMarker;
  isActive: boolean;
  selectSalesPartner: (marker: GoogleMarker) => void;
  standalone?: boolean;
  isPreferred: boolean;
  isDefault: boolean;
  onToggleDefault: (newValue: boolean) => void;
}

const SalesPartnerTile = (props: Props) => {
  const { marker, isActive, selectSalesPartner, className, standalone, isDefault, isPreferred, onToggleDefault } =
    props;

  const { t } = useTranslation();
  const hasINA = containsFeature(marker.features, SalesPartnerFeature.INA);
  const hasFAG = containsFeature(marker.features, SalesPartnerFeature.FAG);

  const cls = classNames("scha__sp__list-card", "gtm-sales-partner-element", className);

  const countryTranslated = t(marker.address.country.name);

  const companyAddress = marker.address.line2 + " " + countryTranslated;

  return (
    <Card
      as={standalone ? "div" : "li"}
      className={cls}
      active={isActive}
      onClick={() => selectSalesPartner(marker)}
      data-test-id={"sales-partner-tile"}
      data-gtm-id={marker.affiliateId}
      data-gtm-name={marker.subsidiaryName}
    >
      <div className={"scha__sp__list-card__data"}>
        {marker.logoUrl && (
          <img className="scha__sp__company-details__company-logo" src={marker.logoUrl} alt="Comapny logo" />
        )}
        {isPreferred && (
          <p className="scha__sp__list-card__preferred-sales-partner">{t("form.preferredSalesPartner.sectionTitle")}</p>
        )}
        <h5 className="scha__sp__list-card__name">{marker.displayName}</h5>
        <p className="scha__sp__list-card__address">{companyAddress}</p>
        <p className="scha__sp__list-card__distance">{marker.formattedDistance !== "" && marker.formattedDistance}</p>
        {isPreferred && (
          <div className={"scha__sp__list-card__default-toggle"}>
            <Switch checked={isDefault} onChange={(_, value) => onToggleDefault(value)} />
            <span>{t("page.salespartner.setDefault")}</span>
          </div>
        )}
        <div className="scha__sp__list-card__competences">
          {marker.productCompetences.map(
            ({ key, label, iconUrl }) =>
              iconUrl && (
                <Tag
                  key={key}
                  variant={"default"}
                  icon={<img src={iconUrl} width={16} height={16} />}
                  iconPosition="start"
                >
                  {""}
                </Tag>
              ),
          )}
          {marker.certifiedCompetences.map(
            ({ key, label, iconUrl }) =>
              iconUrl && (
                <Tag
                  key={key}
                  variant={"default"}
                  icon={<img src={iconUrl} width={16} height={16} />}
                  iconPosition="start"
                >
                  {""}
                </Tag>
              ),
          )}
        </div>
      </div>

      <div className={"scha__sp__list-card__logos"}>
        <Radio className="scha__sp__list-card__check" checked={isActive} name="radio-button" size="medium" />
        {hasINA && <Logos.INA />}
        {hasFAG && <Logos.FAG />}
      </div>
    </Card>
  );
};

export default memo(SalesPartnerTile);
